// GraphQL field declaration for orderDetails — shared by AllOrdersTable's
// ExpandedOrderDetailsPanel/OrderDetailsFetcher (ADR-002) and ActiveOrdersTable's fulfillment
// drawer (ADR-003, OOP-4197). Online/in-store use different variable shapes (mirroring the
// baseline's OnlineOrderDetails/InstoreOrderDetails split) but request the same fields, so this
// module declares one dataModel shared by every variant/consumer.
//
// Selection grounded against the real edge-dev schema (2026-08-07): an initial, speculative
// version (mirroring the baseline JS repo's guessed shape) included fulfillmentGroups.
// estimatedDeliveryDate/isCurbsidePickupAvailable, paymentMethods.type/amount, and top-level
// billingAddress/actions/cancellationRequested; all seven were rejected (FieldUndefined) — not real
// fields on OrderDetails/FulfillmentGroup/PaymentMethod, intentionally omitted here.
// storeNumber/transactionId/registerNumber are `Int` on the real OrderDetailsRequest input (a
// stringified variant, matching the baseline JS repo, was rejected with a variable-type
// ValidationError) — passed through as numbers, matching their natural type in purchaseHistory.
//
// The in-store variant's request shape was CONFIRMED against a real production
// requestOriginatedFrom: 'purchaseHistory' in-store orderDetailsRequest payload (2026-09-25): it
// carries `customerAccountId` (a previous revision omitted it from the in-store request entirely)
// and `searchType: 'instore'` (lowercase — a previous revision sent `'inStore'`, mismatching both
// the real payload and purchaseHistoryToRow.ts's own buildOrderDownloadRequest convention, which
// already used lowercase). Fields the real payload also carries but that are resolver-injected
// server-side metadata, not client-suppliable GraphQL variables (`encryptedOrderNumbers` — the
// client can't produce that encrypted value; `requestOriginatedFrom`/`timezone`/
// `overrideAttributesList`) are deliberately not added here — see
// src/data/adapters/purchaseHistoryToRow.ts's buildOrderDetailsParams for the online/in-store
// routing this variant shape feeds.
//
// `headers.tmxProfileId`/`channelId` and `fulfillmentGroups.fulfillmentDate/storePickup/
// shippingAndHandling/bodfsDeliveryAndHandling` were a SEPARATE, LATER addition (OOP-4197, for
// ActiveOrdersTable's fulfillment drawer) folded into this single query rather than kept as a
// second, isolated dataModel. `fulfillmentGroups[].lineItems` (including
// `RawFulfillmentGroupLineItem`) is CONFIRMED against a real production orderDetails response
// sample (2026-09-25) — line items, including product images, only ever live at
// `fulfillmentGroups[].lineItems[]`, never at an order-root `lineItems` field.
// `storePickup`/`shippingAndHandling`/`bodfsDeliveryAndHandling` remain NOT confirmed against a
// live schema (the real sample validated so far was pickup-only) — best-effort, ported 1:1 from
// @thd-olt-component-react/active-orders (OOP-4059). Because this is now one shared query, a
// rejected/unconfirmed field failing as FieldUndefined would break BOTH AllOrdersTable's and
// ActiveOrdersTable's orderDetails fetch, not just one — see src/data/types.ts's
// RawFulfillmentGroup header comment and
// src/data/adapters/{orderDetailsPanel,fulfillmentDrawer,purchaseHistoryToRow}.ts.
import { params, shape, arrayOf, string, id, number, bool, customType } from '@thd-nucleus/data-sources';

export type OrderDetailsQueryParams =
  | {
      variant: 'online';
      userId: string;
      customerAccountId: string;
      orderNumber: string;
      tmxProfileId?: string;
      channelId?: string;
    }
  | {
      variant: 'inStore';
      userId: string;
      customerAccountId: string;
      storeNumber: number;
      transactionId: number;
      registerNumber: number;
      salesDate: string;
      tmxProfileId?: string;
      channelId?: string;
    };

export function buildOrderDetailsVariables(orderDetailsParams: OrderDetailsQueryParams) {
  const { userId, tmxProfileId, channelId } = orderDetailsParams;
  // `omsVersion` is a GraphQL request field, not a real browser cookie — follows the HeaderExtras
  // pattern activeOrdersDataModel.ts also uses for tmxProfileId/channelId.
  const headers = { tmxProfileId, channelId, omsVersion: 'v2' };

  // Groups fulfillmentGroups by fulfillmentType and has the server pre-filter the response.
  const groupAndSortBy = { groupBy: 'fulfillmentType', filterResponse: true };

  if (orderDetailsParams.variant === 'online') {
    return {
      userId,
      headers,
      request: {
        customerAccountId: orderDetailsParams.customerAccountId,
        orderNumber: orderDetailsParams.orderNumber,
        searchType: 'online',
        groupAndSortBy
      }
    };
  }

  return {
    userId,
    headers,
    request: {
      customerAccountId: orderDetailsParams.customerAccountId,
      storeNumber: orderDetailsParams.storeNumber,
      transactionId: orderDetailsParams.transactionId,
      registerNumber: orderDetailsParams.registerNumber,
      salesDate: orderDetailsParams.salesDate,
      searchType: 'instore',
      groupAndSortBy
    }
  };
}

export const orderDetailsDataModel = {
  orderDetails: params({
    userId: id().isRequired(),
    headers: customType('HeaderExtras').shape({
      tmxProfileId: string(),
      channelId: string(),
      omsVersion: string()
    }),
    request: customType('OrderDetailsRequest!').shape({
      customerAccountId: id(),
      orderNumber: string(),
      storeNumber: number(),
      transactionId: number(),
      registerNumber: number(),
      salesDate: string(),
      searchType: string(),
      groupAndSortBy: shape({ groupBy: string(), filterResponse: bool() })
    })
  }).shape({
    statusCode: string(),
    statusDescription: string(),
    // trackingNumber/workOrderNumber were rejected by the live server (FieldUndefined) — not
    // fields on the live FulfillmentGroup type, so intentionally not requested here (a single
    // invalid field invalidates the whole orderDetails query). This means the deliveryTracker REST
    // request/eligibility gate (useDeliveryTrackerQuery.ts/adapters/deliveryTracker.ts) never has a
    // real value to work with via this query — see RawFulfillmentGroup's header comment in
    // ./types.ts.
    fulfillmentGroups: arrayOf(
      shape({
        fulfillmentType: string(),
        fulfillmentDate: string(),
        storePickup: shape({
          isCurbsidePickupAvailable: bool(),
          IsInLocker: bool(),
          store: shape({
            name: string(),
            number: string(),
            phone: string(),
            address: shape({ line1: string(), city: string(), state: string(), zipCode: string() })
          }),
          pickUpPerson: shape({ name: shape({ firstName: string(), lastName: string() }) })
        }),
        // No `deliveryTrackerData` here — carrier-tracking status/links come from the SEPARATE
        // deliveryTracker REST endpoint (useDeliveryTrackerQuery.ts). ActiveOrdersTable.tsx splices
        // that result into the merged fulfillmentGroup object, not this query. See types.ts's
        // RawFulfillmentGroup header comment.
        shippingAndHandling: shape({
          deliveryAddress: shape({
            name: shape({ firstName: string(), lastName: string() }),
            postalDetails: shape({
              addressLine1: string(),
              addressLine2: string(),
              city: string(),
              state: string(),
              zipCode: string()
            })
          }),
          shipments: shape({ shipment: arrayOf(shape({ estimatedDeliveryDate: string() })) }),
          estEndDeliveryDate: string(),
          applianceDeliveries: arrayOf(shape({ currentDeliveryDate: string(), originalDeliveryDate: string() }))
        }),
        bodfsDeliveryAndHandling: shape({
          bodfsDeliveries: arrayOf(shape({ reservationWindowStartTime: string(), reservationWindowEndTime: string() }))
        }),
        // `lineItems` below (and the deliberate absence of an order-root `lineItems` selection) is
        // CONFIRMED against a real production orderDetails response sample (2026-09-25) — line
        // items, including product images, only live at `fulfillmentGroups[].lineItems[]`.
        lineItems: arrayOf(
          shape({
            thdSku: string(),
            lineId: string(),
            omsId: number(),
            skuNumber: number(),
            statusCode: number(),
            statusDescription: string(),
            brandName: string(),
            description: string(),
            subClassName: string(),
            imageUrl: string(),
            unitPrice: number({ float: true }),
            totalPrice: number({ float: true }),
            currentQuantity: number(),
            orginalOrderedQuantity: number(),
            qtyToPrint: number(),
            pickedUpDate: string(),
            pickupDate: string(),
            deliveredDateArray: arrayOf(string()),
            actions: shape({
              buyAgain: bool(),
              infoAndGuides: bool(),
              returnDropoffAllowed: bool(),
              returnLORPAllowed: bool(),
              returnInStoreAllowed: bool(),
              writeAReview: bool(),
              trackingEnabled: bool()
            })
          })
        )
      })
    ),
    paymentMethods: arrayOf(shape({ lastFour: string() })),
    grandTotalAmount: number({ float: true })
  })
};
